import {cn} from '~/design-system/utils'
import HgAspectRatio from '../../HgAspectRatio'
import HgIcon from '../../HgIcon'
import HgTooltip from '../../HgTooltip'
import {TableTitleSortButton} from './TableTitleSortButton'
import {type HgTableTextContentCell} from '../types'
import {Suspense} from 'react'

const TextContentCell = ({
  title,
  body,
  caption,
  leftIconType,
  rightIconType,
  hintProps,
  image,
  classNames,
  sortProperties,
}: HgTableTextContentCell) => {
  /**
   * In order to nicely wrap our right icons / symbols with their corresponding text, we do some
   * weirdness with wrapping our icons & nbsp characters in a "no white space span", but this
   * increases the chances of text orphans if the "no whitespace" span is present w/o any right
   * icons, so only render that span if there are actually right icons.
   */

  const hasRightContent = Boolean(
    rightIconType ||
      hintProps?.content ||
      (sortProperties?.isSortable && sortProperties?.sortType)
  )

  return (
    <div
      className={cn(
        'flex h-full w-full flex-row items-center space-x-s3 overflow-hidden',
        classNames?.content
      )}
    >
      {image && (
        <HgAspectRatio
          aspectRatio="1:1"
          classNames={{
            wrapper:
              'flex h-full max-h-[38px] min-h-[38px] w-full min-w-[38px] max-w-[38px] md:max-h-[44px] md:min-h-[44px] md:min-w-[44px] md:max-w-[44px] lg:max-h-48 lg:min-h-48 lg:min-w-48 lg:max-w-48 [&>*]:h-full [&>*]:w-full [&_svg]:h-full [&_svg]:w-full',
          }}
        >
          {image}
        </HgAspectRatio>
      )}
      <div className="align-start flex space-x-s2">
        {/**
         *  NB: Icon behavior is a little strange- left icon should always be center aligned w/ the first row of text.
         * However, the right icon, hint, and sort button should all wrap w/ whatever line of text they are on, so this is why there are
         * 2 separate right icon / hint components.
         */}
        {leftIconType && (
          <div className="flex h-24 max-h-24 min-h-24 items-center">
            <HgIcon
              iconType={leftIconType}
              size="small"
              className="fa-fw text-icon-default md:hidden"
            />
            <HgIcon
              iconType={leftIconType}
              size="medium"
              className="fa-fw hidden text-icon-default md:inline-block"
            />
          </div>
        )}
        <div
          className={cn(
            'flex flex-col items-start justify-center justify-items-center',
            classNames?.innerTextContent
          )}
        >
          {title && (
            <span className="text-balance">
              <span className="text-text-default arcadia-heading-9">{title}</span>
              {hasRightContent && (
                <span className="whitespace-nowrap">
                  {rightIconType && (
                    <>
                      <span className="ms-s2">
                        <HgIcon
                          iconType={rightIconType}
                          size="small"
                          className="text-icon-default md:hidden"
                        />
                        <HgIcon
                          iconType={rightIconType}
                          size="medium"
                          className="hidden text-icon-default md:inline-block"
                        />
                      </span>
                    </>
                  )}
                  {hintProps?.content && (
                    <HgTooltip {...hintProps} classNames={{trigger: 'ms-s2'}} />
                  )}

                  {sortProperties?.isSortable && sortProperties?.sortType && (
                    <Suspense fallback={null}>
                      {/** `TableTitleSortButton` uses `useSearchParams` which disables SSR and / or
                       * opts statically rendered pages into CSR without a Suspense boundary:
                       * https://nextjs.org/docs/app/api-reference/functions/use-search-params#static-rendering
                       */}
                      <TableTitleSortButton sortProperties={sortProperties} />
                    </Suspense>
                  )}
                </span>
              )}
            </span>
          )}
          {body && (
            <span className={classNames?.innerTextBody}>
              <span className="text-text-default arcadia-body-2">{body}</span>
              {hasRightContent && (
                <span className="whitespace-nowrap">
                  {rightIconType && !title && (
                    <>
                      <span className="ms-s2">
                        <HgIcon
                          iconType={rightIconType}
                          size="small"
                          className="text-icon-default md:hidden"
                        />
                        <HgIcon
                          iconType={rightIconType}
                          size="medium"
                          className="hidden text-icon-default md:inline-block"
                        />
                      </span>
                    </>
                  )}
                  {hintProps?.content && !title && (
                    <HgTooltip {...hintProps} classNames={{trigger: 'ms-s2'}} />
                  )}
                  {sortProperties?.isSortable && sortProperties?.sortType && (
                    <Suspense fallback={null}>
                      {/** `TableTitleSortButton` uses `useSearchParams` which disables SSR and / or
                       * opts statically rendered pages into CSR without a Suspense boundary:
                       * https://nextjs.org/docs/app/api-reference/functions/use-search-params#static-rendering
                       */}
                      <TableTitleSortButton sortProperties={sortProperties} />
                    </Suspense>
                  )}
                </span>
              )}
            </span>
          )}
          {caption && (
            <p className="flex flex-1 items-center text-text-subdued arcadia-body-4">
              {caption}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default TextContentCell
